import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {lightTheme} from '../../../libs/shared/ui/src/theme/light/lightTheme';
import {UserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {BlockLink} from '../../components/common/links/blockLink';
import {SettingsNavItemCommonStyleProps} from './settingsMenuCommonTypes';

export interface SettingsNavItemProps {
  isActive?: boolean;
  canHover?: boolean;
  isHeader?: boolean;
  icon?: React.ReactNode;
  href?: string;
  onCollapseClick?: Handler;
  isCollapsed?: boolean;
}

interface SettingsNavItemStyleProps {
  $isActive: boolean;
  $canHover: boolean;
  $isHeading: boolean;
  $theme: UserTheme;
}

const commonStyles = (p: SettingsNavItemStyleProps) => css`
  display: grid;
  margin: 0 5px;
  grid-template-areas: '. icon . label . chevron';
  grid-template-columns: 15px 20px 10px minmax(0, 1fr) 15px auto;
  grid-auto-rows: ${p.$isHeading ? '32px' : '28px'};
  border-radius: 4px;
  background-color: ${p.$isActive ? p.$theme.itemBackgroundSelected : 'transparent'};
  margin-bottom: 0;

  &,
  & > div,
  & > div > div {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: normal;
  }

  ${p.$canHover &&
  css`
    &:hover {
      background-color: ${p.$theme.itemBackgroundHover};
    }
  `}
`;
const StyledWrapperDiv = styled.div<SettingsNavItemStyleProps>`
  ${(p) => commonStyles(p)}
`;
const StyledWrapperLink = styled(BlockLink)<SettingsNavItemStyleProps>`
  ${(p) => commonStyles(p)}
`;

const StyledIconWrapperDiv = styled.div<SettingsNavItemCommonStyleProps>`
  grid-area: icon;
  color: ${(p) => p.$theme.icon};
  margin-top: 6px;
  width: 20px;
  height: 20px;
`;

const StyledContentWrapperDiv = styled.div`
  grid-area: label;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

type ChevronStyleProps = {
  $isCollapsed: boolean;
  $theme: UserTheme;
};
const StyledChevronWrapperDiv = styled.div<ChevronStyleProps>`
  ${(p) => css`
    grid-area: chevron;
    margin-right: 15px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    color: ${p.$theme.icon};

    ${hoverSelector} {
      color: ${p.$theme.iconHover};
    }

    ${p.$isCollapsed &&
    css`
      transform: scale(-1);
      transform-origin: center;
    `}
  `}
`;

export const SettingsNavItem: FC<SettingsNavItemProps> = (props) =>
  renderContent(
    props,
    lightTheme,
    <>
      {renderIconPart(props, lightTheme)}
      <StyledContentWrapperDiv>{props.children}</StyledContentWrapperDiv>
      {renderChevronPart(props, lightTheme)}
    </>,
  );

function renderContent(props: SettingsNavItemProps, theme: UserTheme, content: React.ReactNode) {
  if (props.href) {
    return (
      <StyledWrapperLink
        $isActive={Boolean(props.isActive)}
        $canHover={Boolean(props.canHover) && !props.isActive}
        $isHeading={Boolean(props.isHeader)}
        $theme={theme}
        to={props.href}
      >
        {content}
      </StyledWrapperLink>
    );
  }

  return (
    <StyledWrapperDiv
      className={buildHoverParentClassName()}
      $isActive={Boolean(props.isActive)}
      $canHover={Boolean(props.canHover)}
      $isHeading={Boolean(props.isHeader)}
      $theme={theme}
      onClick={props.onCollapseClick}
    >
      {content}
    </StyledWrapperDiv>
  );
}

function renderIconPart(props: SettingsNavItemProps, theme: UserTheme) {
  if (props.icon) {
    return <StyledIconWrapperDiv $theme={theme}>{props.icon}</StyledIconWrapperDiv>;
  }

  return null;
}

function renderChevronPart(props: SettingsNavItemProps, theme: UserTheme) {
  if (!props.onCollapseClick) {
    return null;
  }

  return (
    <StyledChevronWrapperDiv $isCollapsed={Boolean(props.isCollapsed)} $theme={theme}>
      <Icon name="chevronDownSmall" />
    </StyledChevronWrapperDiv>
  );
}
